import React, { forwardRef, useState } from 'react';
import { ViewGridIcon } from '@heroicons/react/outline';
import BottomSheet from './BottomSheet';
import BackButton from './BackButton';
import Button from './Button';
import TextLink from './TextLink';
import { cn } from './cn';

/** Compact household identity with navigation in the bottom sheet. */
const MobileHouseholdHeader = forwardRef(function MobileHouseholdHeader({ title, subtitle, avatars, tabs, active, onChange, onBack, leading, actions, headerActions, className }, ref) {
  const [open, setOpen] = useState(false);
  return (
    <header ref={ref} className={cn('sticky top-0 z-30 border-b border-bun-line bg-bun-surface font-instrument text-bun-text', className)}>
      <div className="flex items-center gap-2 px-3 py-2">
        {leading || (onBack && <BackButton labelHidden onClick={onBack}>Back to clients</BackButton>)}
        {avatars}
        <div className="min-w-0 flex-1">
          <h1 className="m-0 truncate font-instrument text-[16px] font-semibold leading-snug tracking-[-0.01em]">{title}</h1>
          {subtitle && <p className="m-0 mt-px truncate text-bun-meta font-book text-bun-stone">{subtitle}</p>}
        </div>
        <Button variant="secondary" iconOnly aria-label="All household sections" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
          <ViewGridIcon className="h-[18px] w-[18px]" />
        </Button>
      </div>
      {headerActions && <div className="flex items-center justify-end gap-2 border-t border-bun-line px-3 py-2">{headerActions}</div>}
      <BottomSheet open={open} onClose={() => setOpen(false)} aria-label={`${title} sections`} className="max-h-[90dvh] overflow-y-auto pb-[max(18px,env(safe-area-inset-bottom))]">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="m-0 min-w-0 font-instrument text-[18px] font-semibold leading-snug tracking-tight">{title}</h2>
          <TextLink as="button" className="shrink-0 text-bun-body" onClick={() => setOpen(false)}>Done</TextLink>
        </div>
        <nav aria-label="All household sections" data-tooltip="false" className="grid grid-cols-2 gap-2.5">
          {tabs.map(({ id, label, icon: Icon }) => (
            <Button key={id} variant={id === active ? 'primary' : 'secondary'} aria-current={id === active ? 'page' : undefined} className={cn('min-h-[76px] w-full flex-col items-start justify-between gap-2 whitespace-normal rounded-[14px] p-3.5 text-left text-bun-heading font-semibold', id !== active && 'text-bun-text')} onClick={() => { setOpen(false); onChange(id); }}>
              {Icon && <Icon aria-hidden="true" className={cn('h-[18px] w-[18px] shrink-0', id !== active && 'text-bun-slate')} />}
              <span>{label}</span>
            </Button>
          ))}
        </nav>
        {actions && <div className="mt-4 flex flex-wrap gap-2 border-t border-bun-line pt-4">{actions}</div>}
      </BottomSheet>
    </header>
  );
});

export default MobileHouseholdHeader;
